import React, { useEffect } from 'react';
import DeleteButton from 'components/common/componentstyles/deleteButton';
import CustomObjectTemplate from './CustomObjectTemplate';
import { LIST_DELETE_ICON_SIZE } from './CustomArrayFieldTemplate';
import CustomCheckbox from './CustomCheckbox';
import { useTranslation } from 'react-i18next';
import { buttonClasses } from 'components/common/tailwindClassnames';
import Select from 'react-select';
import { defaultStyle } from 'components/common/componentstyles/selectinputstyles';

type ArrayElementType = string | { key: string; value: any };
interface CustomArrayFieldProps {
  schema: any;
  formData: any;
  onChange: (value: any) => void;
  // rjsf passes this to every custom field; drawn as the same red asterisk as
  // the other required fields (generateUISchema, CustomArrayFieldTemplate).
  required?: boolean;
  // Set when the form is locked (e.g. its action is switched off).
  disabled?: boolean;
  readonly?: boolean;
}

export const CustomArrayField: React.FC<CustomArrayFieldProps> = ({
  schema,
  formData,
  onChange,
  required,
  disabled,
  readonly,
}) => {
  const { title, items } = schema;
  const locked = !!(disabled || readonly);
  const itemsData = formData || [];
  const { t } = useTranslation();

  // Fixed-choice multi-select options rendered as a checkbox/switch list.
  // Two schema shapes produce them: `items.oneOf` where every entry is a
  // {const, title} pair (the shape connectors use for labeled options, since
  // ajv strict mode rejects enumNames) and `items.enum` with `uniqueItems: true`
  // (value doubles as label). A oneOf of variant subschemas (no `const`) is not
  // a fixed-choice list and falls through to the add/remove item editor.
  const selectOptions: { value: any; label: string }[] | null =
    Array.isArray(items?.oneOf) &&
    items.oneOf.every((option: any) => option && 'const' in option)
      ? items.oneOf.map((option: any) => ({
          value: option.const,
          label: option.title ?? String(option.const),
        }))
      : schema.uniqueItems && Array.isArray(items?.enum)
        ? items.enum.map((enumItem: any) => ({
            value: enumItem,
            label: String(enumItem),
          }))
        : null;

  // Files vars
  const accept = 'text/xml'; // Default to XML files (for now)

  useEffect(() => {
    // Ensure that formData is initialized properly
    if (!formData) {
      onChange([]);
    }
  }, [formData, onChange]);

  const handleAddItem = () => {
    let newItem: any;
    if (
      typeof items === 'object' &&
      items !== null &&
      'enum' in items &&
      items.enum
    ) {
      newItem = items.enum[0]; // Default to the first enum value
    } else {
      newItem = ''; // Default to an empty string for arrays of strings
    }
    const updatedItems = [...itemsData, newItem];
    onChange(updatedItems);
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = [...itemsData];
    updatedItems.splice(index, 1);
    onChange(updatedItems);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const files = Array.from(event.target.files || []);
    const xmlFiles = files.filter((file) => file.type === 'text/xml');

    const updatedItems = [...itemsData];
    updatedItems[index] = xmlFiles.length > 0 ? xmlFiles[0] : null;
    onChange(updatedItems);
  };

  return (
    <div className="my-2 w-full">
      {/* Everything is centred on the title and description, like every other
          control in these forms (see HorizontalFieldTemplate). A checkbox list
          sits in the right half beside them. An add/remove list matches
          CustomArrayFieldTemplate: Add button beside the title, items below. */}
      <div
        className={`flex ${selectOptions ? 'flex-row items-center justify-between gap-6' : 'flex-col'} mb-4 mt-4 w-full`}
      >
        <div
          className={`flex flex-row items-center justify-between gap-6 ${selectOptions ? 'w-1/2' : 'w-full'}`}
        >
          <div
            className={`${selectOptions ? 'w-full' : 'w-1/2'} flex flex-col`}
          >
            <label className="font-semibold">
              {title}
              {required && (
                <span className="ml-1 font-normal text-ui-status-error-destructive">
                  *
                </span>
              )}
            </label>
            {schema && schema.description && (
              <div className="mt-1">
                <span className="text-sm text-ui-text-primary-text">
                  {schema.description}
                </span>
              </div>
            )}
          </div>
          {!selectOptions && (
            <div className="flex w-1/2 justify-end">
              <button
                type="button"
                className={buttonClasses.listAdd}
                onClick={handleAddItem}
                disabled={locked}
              >
                {t('Add Item')}
              </button>
            </div>
          )}
        </div>
        <div className={selectOptions ? 'w-1/2' : 'mt-2 w-full'}>
          <ul className="w-full">
            {selectOptions
              ? selectOptions.map((option) => (
                  <li key={String(option.value)} className="flex flex-col py-1">
                    <CustomCheckbox
                      label={option.label}
                      itemsData={itemsData}
                      enumItem={option.value}
                      onChange={(checked) => {
                        onChange(
                          checked
                            ? [...itemsData, option.value]
                            : itemsData.filter(
                                (item: any) => item !== option.value,
                              ),
                        );
                      }}
                      formData={formData}
                      disabled={locked}
                    />
                  </li>
                ))
              : itemsData.map((item: ArrayElementType, index: number) => (
                  <li
                    key={index}
                    className="mb-3 flex w-full flex-row items-center transition-all duration-300"
                  >
                    {typeof items === 'object' &&
                    items !== null &&
                    'enum' in items ? (
                      <div className="flex-1">
                        {/* Same react-select look as the reference dropdowns
                          (ReferenceSelectWidget), not a bare native <select>. */}
                        <Select
                          options={(items.enum ?? []).map((enumItem: any) => ({
                            value: enumItem,
                            label: String(enumItem),
                          }))}
                          value={(() => {
                            const current =
                              typeof item === 'string' ? item : item.key;
                            return { value: current, label: String(current) };
                          })()}
                          onChange={(option: any) => {
                            const value = option?.value;
                            const updatedItems = [...itemsData];
                            updatedItems[index] =
                              typeof item === 'string'
                                ? value
                                : { key: value, value: value };
                            onChange(updatedItems);
                          }}
                          isSearchable={false}
                          isDisabled={locked}
                          menuPortalTarget={
                            typeof document !== 'undefined'
                              ? document.body
                              : undefined
                          }
                          styles={{
                            ...defaultStyle,
                            menuPortal: (provided: any) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </div>
                    ) : typeof items === 'object' &&
                      items !== null &&
                      items.type === 'object' ? (
                      <div className="flex-1">
                        <CustomObjectTemplate
                          schema={items}
                          formData={
                            typeof item === 'object' && item !== null
                              ? item
                              : {}
                          }
                          onChange={(value: any) => {
                            const updatedItems = [...itemsData];
                            updatedItems[index] = value;
                            onChange(updatedItems);
                          }}
                          disabled={locked}
                        />
                      </div>
                    ) : (
                      <div className="flex-1">
                        <input
                          type="text"
                          className="w-full rounded border border-unlisted-gray-lines px-2 py-1 text-ui-text-primary-text transition-all duration-300"
                          value={(item as string) || ''}
                          placeholder={t('Enter your text here') || ''}
                          disabled={locked}
                          onChange={(e) => {
                            const updatedItems = [...itemsData];
                            updatedItems[index] = e.target.value;
                            onChange(updatedItems);
                          }}
                        />
                      </div>
                    )}
                    <DeleteButton
                      onClick={() => handleRemoveItem(index)}
                      iconSize={LIST_DELETE_ICON_SIZE}
                      disabled={locked}
                    />
                  </li>
                ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
